import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NotificationsOutlined } from '@mui/icons-material';
import authService from '../../../services/authService';
import './NotificationCenter.css';

// ── Groupe par type ──────────────────────────────────────────────
const CONNECT_TYPES = ['user.online', 'auth.login', 'user.login'];

const getGroup = (type) => {
  if (type === 'model.used') return 'sim';
  return CONNECT_TYPES.includes(type) ? 'online' : 'offline';
};

const GROUP_NAV = {
  online:  'userLogs',
  sim:     'secufireActionLogs',
  offline: 'userLogs',
};

const GROUP_META = {
  online:  { label: 'Connexion',   dotColor: '#22c55e', pillBg: '#dcfce7', pillColor: '#16a34a' },
  sim:     { label: 'Simulation',  dotColor: '#be1622', pillBg: '#fee2e2', pillColor: '#be1622' },
  offline: { label: 'Déconnexion', dotColor: '#9ca3af', pillBg: '#f1f5f9', pillColor: '#64748b' },
};

const GROUP_ORDER = ['online', 'sim', 'offline'];

// ── Icône SVG inline ─────────────────────────────────────────────
const NotifIcon = ({ type }) => {
  if (type === 'model.used') {
    return (
      <span className="nc-icon nc-icon--sim">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8.5 14.5A5.5 5.5 0 0 0 19 11c0-5-5-9-5-9s0 4-4 6.5C8.5 10 7 11.5 7 13.5a5 5 0 0 0 1.5 1Z"/>
          <path d="M12 18a3 3 0 0 0 0-6"/>
        </svg>
      </span>
    );
  }
  const isOnline = CONNECT_TYPES.includes(type);

  return (
    <span className={`nc-icon ${isOnline ? 'nc-icon--online' : 'nc-icon--offline'}`}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
      <span className={`nc-icon-badge ${isOnline ? 'nc-icon-badge--on' : 'nc-icon-badge--off'}`} />
    </span>
  );
};

// ── Formatage Date + Heure (ex: 22 avr. 14:21) ──────────────────
const fmtTime = (iso) => {
  try {
    const date = new Date(iso);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(',', '');
  } catch { return ''; }
};

const buildMsg = (n) => {
  if (n.type === 'model.used') {
    const d = n.data || {};
    return {
      main: `${d.username || 'Utilisateur'}${d.ref ? ` — ${d.ref}` : ''}`,
      sub:  [d.model_type, d.location].filter(Boolean).join(' · '),
    };
  }
  return {
    main: n.message || n.title || '',
    sub:  n.data?.source ? `via ${n.data.source}` : '',
  };
};

const NotificationCenter = ({ onNavigate, registerPush, disabled = false  }) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [fetched, setFetched] = useState(false);
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  const totalUnread = notifications.filter((n) => !n.is_read).length;

  useEffect(() => {
    authService.getNotifications(100)
      .then((res) => {
        setNotifications(res?.notifications || []);
        setFetched(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!registerPush) return;
    registerPush((notif) => {
      setNotifications((prev) => {
        if (prev.find((n) => n.id === notif.id)) return prev;
        return [notif, ...prev];
      });
    });
  }, [registerPush]);

  const fetchNotifications = useCallback(async () => {
    if (fetched) return;
    setLoading(true);
    try {
      const res = await authService.getNotifications(50);
      setNotifications(res?.notifications || []);
      setFetched(true);
    } catch (e) {
      console.error('NotificationCenter fetch:', e);
    } finally {
      setLoading(false);
    }
  }, [fetched]);

  const toggle = useCallback(() => {
    setOpen((v) => {
      if (!v) fetchNotifications();
      return !v;
    });
  }, [fetchNotifications]);

  const markAllRead = useCallback(async (e) => {
    e.stopPropagation();
    try {
      await authService.markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (err) { console.error(err); }
  }, []);

  const handleItemClick = useCallback(async (notif) => {
    if (!notif.is_read) {
      try {
        await authService.markNotificationRead(notif.id);
        setNotifications((prev) => prev.map((n) => n.id === notif.id ? { ...n, is_read: true } : n));
      } catch (err) { console.error(err); }
    }
    setOpen(false);
    onNavigate?.(GROUP_NAV[getGroup(notif.type)]);
  }, [onNavigate]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) &&
          btnRef.current && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = notifications.filter((n) =>
    filter === 'all' || getGroup(n.type) === filter
  );

  const grouped = GROUP_ORDER.reduce((acc, g) => {
    const items = filtered.filter((n) => getGroup(n.type) === g);
    if (items.length) acc[g] = items;
    return acc;
  }, {});

  const footerNav = filter === 'sim'
    ? { label: 'Voir les logs SecuFire Action', section: 'secufireActionLogs' }
    : (filter === 'online' || filter === 'offline')
    ? { label: 'Voir les logs Utilisateurs', section: 'userLogs' }
    : { label: 'Voir tous les logs', section: 'userLogs' };

  const cap = totalUnread > 99 ? '99+' : String(totalUnread);

  return (
    <div className="nc-wrap" style={disabled ? { visibility: 'hidden', pointerEvents: 'none' } : {}}>
      <button
        ref={btnRef}
        className={`nc-bell${open ? ' nc-bell--open' : ''}`}
        onClick={toggle}
        aria-label={`Notifications${totalUnread > 0 ? ` — ${cap} non lues` : ''}`}
      >
        <NotificationsOutlined style={{ fontSize: 28 }} />
        {totalUnread > 0 && <span className="nc-badge">{cap}</span>}
      </button>

      {open && (
        <div ref={panelRef} className="nc-panel" role="dialog">
          <div className="nc-head">
            <span className="nc-head-title">Notifications</span>
            <button className="nc-mark-all" onClick={markAllRead}>Tout marquer lu</button>
          </div>

          <div className="nc-filters">
            {[{ id: 'online', label: 'Connexion' }, { id: 'sim', label: 'Simulation' }, { id: 'offline', label: 'Déconnexion' }]
              .map((f) => (
                <button
                  key={f.id}
                  className={`nc-filter${filter === f.id ? ' nc-filter--active' : ''}`}
                  onClick={() => setFilter(filter === f.id ? 'all' : f.id)}
                >{f.label}</button>
              ))}
          </div>

          <div className="nc-list">
            {loading && <div className="nc-empty">Chargement...</div>}
            {!loading && filtered.length === 0 && <div className="nc-empty">Aucune notification</div>}

            {!loading && Object.entries(grouped).map(([groupId, items]) => {
              const meta = GROUP_META[groupId];
              const groupUnread = items.filter((n) => !n.is_read).length;
              return (
                <div key={groupId}>
                  <div className="nc-group-head">
                    <span className="nc-group-dot" style={{ background: meta.dotColor }} />
                    <span className="nc-group-label">{meta.label}</span>
                    {groupUnread > 0 && (
                      <span className="nc-group-count" style={{ background: meta.pillBg, color: meta.pillColor }}>
                        {groupUnread}
                      </span>
                    )}
                  </div>

                  {items.map((n) => {
                    const msg = buildMsg(n);
                    return (
                      <div
                        key={n.id}
                        className={`nc-item${!n.is_read ? ' nc-item--unread' : ''}`}
                        onClick={() => handleItemClick(n)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleItemClick(n)}
                      >
                        <NotifIcon type={n.type} />
                        <div className="nc-item-body">
                          <div className="nc-item-msg">{msg.main}</div>
                          {msg.sub && <div className="nc-item-sub">{msg.sub}</div>}
                        </div>
                        <div className="nc-item-meta">
                          <span className="nc-item-time">{fmtTime(n.created_at)}</span>
                          {!n.is_read && <span className="nc-item-dot" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          <div className="nc-footer">
            <button className="nc-see-all" onClick={() => { setOpen(false); onNavigate?.(footerNav.section); }}>
              {footerNav.label} →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;